import React, {useState, useRef, useEffect, useLayoutEffect} from 'react';
import QRCode from 'qrcode';
import PaymentStepActions from '../PaymentStepActions/PaymentStepActions';
import CustomInput from '../../../CustomInput/CustomInput';
import Phone from '../../../../helpers/Phone';
import Debug from '../../../../helpers/Debug';
import './DynamicForm.css';

function DynamicForm({
    steps,
    actionButtonBackgroundColor,
    actionButtonBorderColor,
    actionButtonLabelColor,
    cancelButtonBackgroundColor,
    cancelButtonBorderColor,
    cancelButtonLabelColor,
    backButtonBackgroundColor,
    backButtonBorderColor,
    backButtonLabelColor,
    customInputOutline,
    customInputClearButtonBackgroundColor,
    customInputClearButtonLabelColor,
    onClose,
    onFinished
}) {
    const sortedSteps = [...(steps || [])].sort((a, b) => (a.position || 0) - (b.position || 0));
    const [stepIndex, setStepIndex] = useState(0);
    const [direction, setDirection] = useState('next');
    const [values, setValues] = useState({});
    const [errors, setErrors] = useState({});
    const [linkTooltip, setLinkTooltip] = useState(undefined);
    const [contentHeight, setContentHeight] = useState(undefined);
    const qrCanvasRef = useRef(null);
    const contentRef = useRef(null);
    const formRef = useRef(null);

    const currentStep = sortedSteps[stepIndex];
    const sortedFields = currentStep ? [...(currentStep.fields || [])].sort((a, b) => (a.position || 0) - (b.position || 0)) : [];

    // Keeps the wrapper's height in sync with its content, so height changes (step changes, validation errors appearing) can be CSS-transitioned instead of snapping instantly.
    useLayoutEffect(() => {
        const element = contentRef.current;
        if (!element) return;
        const observer = new ResizeObserver(() => setContentHeight(element.scrollHeight));
        observer.observe(element);
        return () => observer.disconnect();
    }, [stepIndex]);

    useEffect(() => {
        setLinkTooltip(undefined);
    }, [stepIndex]);

    useEffect(() => {
        if (linkTooltip && qrCanvasRef.current) {
            QRCode.toCanvas(qrCanvasRef.current, linkTooltip.url, {width: 140, height: 140}, (error) => {
                if (error) Debug.printToLog('error', error);
            });
        }
    }, [linkTooltip]);

    const handleFieldChange = (fieldId, value) => {
        setValues(previous => ({...previous, [fieldId]: value}));
        setErrors(previous => ({...previous, [fieldId]: undefined}));
    };

    const validateField = (field, value) => {
        const validation = field.validation || {};
        if (field.type === 'checkbox') {
            if (validation.required && !value) return 'Debes aceptar esta condición para continuar.';
            return undefined;
        }
        if (validation.required && (!value || String(value).trim() === '')) return 'Este campo es obligatorio.';
        if (value && validation.minLength && String(value).length < validation.minLength) return `Debe tener al menos ${validation.minLength} caracteres.`;
        if (value && validation.pattern && !(new RegExp(validation.pattern).test(value))) return 'El formato ingresado no es válido.';
        if (value && field.type === 'phone' && !validation.pattern && !Phone.valid(value)) return 'El teléfono ingresado no es válido.';
        return undefined;
    };

    const validateStep = () => {
        const newErrors = {};
        sortedFields.forEach(field => {
            const message = validateField(field, values[field.id]);
            if (message) newErrors[field.id] = message;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validateStep()) return;
        if (stepIndex < sortedSteps.length - 1) {
            setDirection('next');
            setStepIndex(stepIndex + 1);
        } else if (onFinished) onFinished(values);
    };

    const handleBack = () => {
        if (stepIndex > 0) {
            setDirection('back');
            setStepIndex(stepIndex - 1);
        }
    };

    const handleClose = () => {
        if (onClose) onClose('custom-close-button');
    };

    const handleLinkClick = (event, url) => {
        if (linkTooltip && linkTooltip.url === url) {
            setLinkTooltip(undefined);
            return;
        }
        // Positioned relative to the form's own box (not the viewport) so it isn't thrown off by any transform an ancestor (e.g. Modal's centering transform) applies to establish its own containing block.
        const linkRect = event.currentTarget.getBoundingClientRect();
        const formRect = formRef.current.getBoundingClientRect();
        setLinkTooltip({
            url,
            top: linkRect.top - formRect.top,
            left: linkRect.left - formRect.left + linkRect.width / 2
        });
    };

    const renderTextWithLinks = (text) => {
        const parts = [];
        const regex = /\[([^\]]+)]\(([^)]+)\)/g;
        let lastIndex = 0;
        let match;
        let key = 0;
        while ((match = regex.exec(text)) !== null) {
            if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
            const url = match[2];
            parts.push(<span key={key++} className='dynamic-form-link' onClick={(event) => handleLinkClick(event, url)}>{match[1]}</span>);
            lastIndex = regex.lastIndex;
        }
        if (lastIndex < text.length) parts.push(text.slice(lastIndex));
        return parts;
    };

    const renderTextField = (field) => {
        return (
            <div className='dynamic-form-field' key={field.id}>
                <CustomInput
                    id={`dynamic-form-${field.id}`}
                    label={field.label}
                    insetLabel={true}
                    placeholder={field.placeholder}
                    showClearButton={true}
                    value={values[field.id] || ''}
                    focusBorderColor={customInputOutline}
                    clearButtonBackgroundColor={customInputClearButtonBackgroundColor}
                    clearButtonLabelColor={customInputClearButtonLabelColor}
                    isAddressInput={false}
                    disabled={false}
                    onChange={(data) => handleFieldChange(field.id, data.value)}
                />
                {errors[field.id] && <p className='dynamic-form-field-error'>{errors[field.id]}</p>}
            </div>
        );
    };

    const renderCheckboxField = (field) => {
        return (
            <div className='dynamic-form-checkbox-field' key={field.id}>
                <label className='dynamic-form-checkbox-label'>
                    <input
                        type='checkbox'
                        checked={!!values[field.id]}
                        onChange={(e) => handleFieldChange(field.id, e.target.checked)}
                    />
                    <span>{field.label}</span>
                </label>
                {errors[field.id] && <p className='dynamic-form-field-error'>{errors[field.id]}</p>}
            </div>
        );
    };

    const renderField = (field) => {
        switch (field.type) {
            case 'text':
            case 'phone':
                return renderTextField(field);
            case 'checkbox':
                return renderCheckboxField(field);
            default:
                return null;
        }
    };

    const renderFooter = () => {
        const footer = currentStep.footer;
        if (!footer) return null;
        return (
            <p className='dynamic-form-footer'>{renderTextWithLinks(footer.text)}</p>
        );
    };

    const renderLinkTooltip = () => {
        if (!linkTooltip) return null;
        return (
            <div className='dynamic-form-link-tooltip' style={{top: linkTooltip.top - 12, left: linkTooltip.left}}>
                <canvas ref={qrCanvasRef} className='dynamic-form-link-tooltip-canvas'/>
                <p className='dynamic-form-link-tooltip-label'>Escanea el código para ver el enlace en tu dispositivo</p>
            </div>
        );
    };

    if (!currentStep) return null;

    return (
        <div className='dynamic-form' ref={formRef}>
            <div className='dynamic-form-viewport' style={{height: contentHeight}}>
                <div ref={contentRef} key={stepIndex} className={`dynamic-form-step dynamic-form-step-${direction}`}>
                    {currentStep.title && <h2 className='dynamic-form-title'>{currentStep.title}</h2>}
                    {currentStep.description && <h3 className='dynamic-form-description'>{currentStep.description}</h3>}
                    <div className='dynamic-form-fields'>
                        {sortedFields.map(field => renderField(field))}
                    </div>
                    {renderFooter()}
                </div>
            </div>
            {renderLinkTooltip()}
            <PaymentStepActions
                submitLabel='Continuar'
                cancelButtonBackgroundColor={cancelButtonBackgroundColor}
                cancelButtonBorderColor={cancelButtonBorderColor}
                cancelButtonLabelColor={cancelButtonLabelColor}
                backButtonBackgroundColor={backButtonBackgroundColor}
                backButtonBorderColor={backButtonBorderColor}
                backButtonLabelColor={backButtonLabelColor}
                submitButtonBackgroundColor={actionButtonBackgroundColor}
                submitButtonBorderColor={actionButtonBorderColor}
                submitButtonLabelColor={actionButtonLabelColor}
                showCloseButton={stepIndex === 0}
                showBackButton={stepIndex > 0}
                showSubmitButton={true}
                onClose={handleClose}
                onBack={handleBack}
                onSubmit={handleSubmit}
            />
        </div>
    );
}

export default DynamicForm;
